import PageLayout from "@/components/layout/PageLayout";
import HeroSlider from "@/components/home/HeroSlider";
import CTASection from "@/components/home/CTASection";
import ServizioCivileBanner from "@/components/home/ServizioCivileBanner";
import CorsoSoccorritoreBanner from "@/components/home/CorsoSoccorritoreBanner";
import LavoraConNoiBanner from "@/components/home/LavoraConNoiBanner";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Ambulance, Phone, HeartPulse, Calendar, GraduationCap, ArrowRight, Users, Clock, Truck, Heart, Stethoscope, Copy } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import Picture from "@/components/ui/picture";
import { toast } from "sonner";

const CODICE_FISCALE = "87011130157";

const serviceImages = [
  "/images/1270x710_124.jpg.jpg", "/images/1270x710_125.jpg.jpg", "/images/1270x710_126.jpg.jpg",
  "/images/1270x710_92.jpg.jpg", "/images/1270x710_29.png.jpg", "/images/1270x710_356.jpg.jpg",
];
const serviceIcons = [HeartPulse, Ambulance, Phone, Calendar, GraduationCap, Stethoscope];
const serviceLinks = [
  "/servizi/emergenza-sanitaria-112", "/servizi/trasporto-sanitario-programmato",
  "/servizi/teleassistenza", "/servizi/assistenza-manifestazioni-eventi", "/servizi/corsi-formazione-sanitaria", "/servizi/assistenza-guardia-medica",
];
const serviceCtaActions = ["tel:112", "tel:0396612112", "mailto:segreteria@avps.it", "mailto:segreteria@avps.it", "mailto:segreteria@avps.it", "tel:116117"];
const statIcons = [Users, Clock, Truck, Heart];
const galleryImages = [
  { src: "/images/1270x710_358.jpg.jpg", alt: "AVPS volunteers" },
  { src: "/images/1270x710_127.jpg.jpg", alt: "AVPS night shift" },
  { src: "/images/1270x710_148.jpg.jpg", alt: "First aid training" },
];

const Index = () => {
  const { t } = useLanguage();

  return (
    <PageLayout>
      <HeroSlider />

      {/* Banner 5x1000 */}
      <section className="bg-accent/10 border-y border-accent/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
              <Heart className="w-6 h-6 text-accent" />
            </div>
            <div>
              <p className="font-heading font-bold text-sm sm:text-base">{t.index.fivexTitle}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{t.index.fivexText}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-background rounded-xl px-5 py-3 shadow-sm border border-border">
            <div className="text-center">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.index.fivexCf}</span>
              <p className="font-heading font-extrabold text-lg text-primary tracking-wider select-all">{CODICE_FISCALE}</p>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(CODICE_FISCALE); toast.success("Codice fiscale copiato!"); }} className="p-2 rounded-lg hover:bg-secondary transition-colors" aria-label="Copia">
              <Copy className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </section>

      <CorsoSoccorritoreBanner variant="compact" />
      <ServizioCivileBanner variant="compact" />
      <LavoraConNoiBanner />
      <section className="py-16 md:py-24 px-4 sm:px-6 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold text-accent uppercase tracking-widest">{t.index.servicesLabel}</span>
              <h2 className="mt-3">{t.index.servicesTitle}</h2>
              <p className="mt-3 text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">{t.index.servicesSubtitle}</p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.index.services.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <ScrollReveal key={i} delay={i * 80} direction="scale">
                  <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col group transition-all duration-300 hover:shadow-lg hover:-translate-y-1.5 h-full">
                    <div className="relative h-44 overflow-hidden">
                      <Picture src={serviceImages[i]} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-lg bg-primary/90 flex items-center justify-center shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                        <Icon className="w-5 h-5 text-primary-foreground" />
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-base font-bold mb-2 font-heading group-hover:text-primary transition-colors duration-200">{s.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1 leading-relaxed">{s.desc}</p>
                      <div className="space-y-2">
                        <a href={serviceCtaActions[i]} className="text-sm font-semibold text-accent hover:underline block">{s.cta}</a>
                        <Link to={serviceLinks[i]} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:gap-2 transition-all duration-200">
                          {t.index.learnMore} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 sm:px-6 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ScrollReveal direction="left">
              <div>
                <span className="text-xs font-semibold text-accent uppercase tracking-widest">{t.index.historyLabel}</span>
                <h2 className="mt-3">{t.index.historyTitle}</h2>
                <p className="mt-5 text-muted-foreground leading-relaxed text-base">{t.index.historyText}</p>
                <div className="grid grid-cols-2 gap-5 mt-8">
                  {t.index.stats.map((s, i) => {
                    const Icon = statIcons[i];
                    return (
                      <div key={i} className="flex items-center gap-3 group/stat">
                        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 transition-all duration-300 group-hover/stat:bg-primary/20 group-hover/stat:scale-110">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-heading font-extrabold text-xl text-foreground">{s.value}</div>
                          <div className="text-xs text-muted-foreground">{s.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button variant="default" size="lg" className="mt-8 transition-all duration-300 hover:shadow-lg" asChild>
                  <Link to="/chi-siamo/storia">{t.index.historyBtn}</Link>
                </Button>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="right" delay={150}>
              <div className="relative group/img">
                <Picture src="/images/1140x640_3991.jpg.jpg" alt="AVPS volunteers" className="rounded-2xl w-full object-cover aspect-[4/3] shadow-xl transition-transform duration-500 group-hover/img:scale-[1.02]" loading="lazy" />
                <div className="absolute -bottom-4 -left-4 bg-accent text-accent-foreground px-6 py-3 rounded-xl font-heading font-bold text-lg shadow-lg transition-transform duration-300 group-hover/img:translate-y-[-4px]">{t.index.since}</div>
              </div>
            </ScrollReveal>
          </div>
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {galleryImages.map((img, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="relative rounded-xl overflow-hidden aspect-[16/10] group/gallery">
                  <Picture src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover/gallery:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent opacity-0 group-hover/gallery:opacity-100 transition-opacity duration-300" />
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </PageLayout>
  );
};

export default Index;
